import { useEffect, useMemo, useState } from 'react'
import LocalStorage from '../../services/LocalStorage'
import {
  EventDoc,
  GroupDoc,
  GroupParticipant,
  GroupParticipantStatus,
  GroupParticipantType,
  ParticipantDataDoc,
  ProfileDoc
} from '../../types/api/changes'
import { LocalStorageKey } from '../../types/services/localStorage'
import * as Styled from './styled'
import { withObservables } from '@nozbe/watermelondb/react'
import ObserveService from '../../services/db/Observe'
import { Model } from '../../types/model'
import { ParticipantsActionsPopupContent } from '../popups/groupInfo/participantActions/ParticipantsActionPopup'
import { Box, ClickAwayListener } from '@mui/material'
import { GroupCardAvatarContainer } from 'components/cards/styled'
import Avatar from 'components/badges/Avatar'
import { getRandomColor } from 'utils/colors'
import { useProgress } from 'components/providers/ProgressProvider'
import { CaretDownIcon } from 'assets/icons'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { t } from 'i18next'
import {
  ParticipantsForGroup,
  localGroupById
} from 'services/db/Queries/GroupQueries'
import { IEventDetails } from 'hooks/useEventDetails'
import i18n from 'i18n'
import Popup from 'components/popup/Popup'
import { isUserAdminInGroup } from 'utils/api/groups'
import { isConvertedGroup } from 'utils/groups'

export const MEMBERS_THRESHOLD = 3

interface MemberMenu {
  anchorEl: HTMLElement | null
  participant: ParticipantDataDoc
}

export function MembersList(props: MemberListProps) {
  const progress = useProgress()
  const [showExpander, setShowExpander] = useState(true)
  const [participants, setParticipants] = useState<
    Record<string, GroupParticipant>
  >(props.group?.Participants ?? props.participants ?? {})
  const [membersGroup, setMembersGroup] = useState<
    Model<GroupDoc> | undefined | GroupDoc
  >(props.group)

  const [participantsData, setParticipantsData] = useState(
    props.localParticipants ?? []
  )
  const participantsAmount = Object.values(participants).filter(
    (participant) => participant.Status === GroupParticipantStatus.ACTIVE
  ).length

  const pendingParticipantsAmount = Object.keys(
    props.group?.PendingParticipants ?? {}
  ).length

  const overallAmount = participantsAmount + pendingParticipantsAmount

  let pendingSlice = undefined

  if (participantsAmount < MEMBERS_THRESHOLD && showExpander) {
    pendingSlice = MEMBERS_THRESHOLD - participantsAmount
  }

  const showAllMembers = () => {
    setShowExpander(false)
  }
  const participantsToDraw = participantsData
    .sort((a, b) => {
      const currentUserId = LocalStorage.get(LocalStorageKey.USER_ID)

      const aIsOwner = a.AccountID === membersGroup?.OwnerID
      const bIsOwner = b.AccountID === membersGroup?.OwnerID

      const aIsAdmin = isUserAdminInGroup(membersGroup, a.AccountID)
      const bIsAdmin = isUserAdminInGroup(membersGroup, b.AccountID)

      const aIsYou = a.AccountID === currentUserId
      const bIsYou = b.AccountID === currentUserId

      // Owners first
      if (aIsOwner && !bIsOwner) return -1
      if (!aIsOwner && bIsOwner) return 1

      // Then admins
      if (aIsAdmin && !bIsAdmin) return -1
      if (!aIsAdmin && bIsAdmin) return 1

      // Then "You"
      if (aIsYou && !bIsYou) return -1
      if (!aIsYou && bIsYou) return 1

      // Others stay in original order
      return 0
    })
    .slice(
      0,
      showExpander
        ? participantsAmount > MEMBERS_THRESHOLD // if we have participants more than threshold, we show threshold
          ? MEMBERS_THRESHOLD
          : undefined // otherwise we show all participants
        : undefined // in case no expander we also show all participants
    )

  useEffect(() => {
    if (props.groupId)
      localGroupById(props.groupId).then((group) => {
        setParticipants(group?.Participants ?? {})
        setMembersGroup(group)
        ParticipantsForGroup(Object.keys(group?.Participants ?? {}))
          .fetch()
          .then((participants) => {
            setParticipantsData(participants)
          })
        return group
      })
  }, [props.groupId])

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | MemberMenu>(null)

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={() => {
        setMenuAnchorEl(null)
      }}
    >
      <span>
        <Box marginTop={2} />
        <Styled.MembersList id="membersList">
          {participantsToDraw.map((participant) => {
            return (
              <div key={participant.AccountID} style={{ width: '100%' }}>
                <MemberListRow
                  eventHook={props.eventHook}
                  actionsOnEvent={props.actionsOnEvent}
                  profile={props.profile}
                  participant={participant}
                  group={membersGroup}
                  event={props.event}
                  setLoading={props.setLoading}
                  withoutActions={props.withoutActions}
                  menuAnchorEl={menuAnchorEl}
                  setMenuAnchorEl={setMenuAnchorEl}
                  participants={participants}
                  progress={progress}
                />
              </div>
            )
          })}

          {!showExpander || participantsAmount < MEMBERS_THRESHOLD ? (
            Object.keys(props.group?.PendingParticipants ?? {})
              .slice(0, pendingSlice)
              .map((phoneNumber) => {
                return (
                  <div
                    key={phoneNumber}
                    style={{
                      marginLeft: i18n.dir() === 'ltr' ? 4 : 0,
                      marginRight: i18n.dir() === 'ltr' ? 0 : 4
                    }}
                  >
                    <PendingMember phoneNumber={phoneNumber} />
                  </div>
                )
              })
          ) : (
            <></>
          )}
          {overallAmount > MEMBERS_THRESHOLD && showExpander ? (
            <div onClick={showAllMembers} style={{ cursor: 'pointer' }}>
              <Styled.MemberListItem style={{ marginBottom: 16 }}>
                <Styled.MembersListNameContainer columnGap={1}>
                  <span
                    style={{
                      marginLeft: i18n.dir() === 'ltr' ? 34 : 0,
                      marginRight: i18n.dir() === 'ltr' ? 0 : 34,
                      fontFamily: 'NotoSans',
                      fontSize: '14px'
                    }}
                  >
                    {t('membersAmount', {
                      members: overallAmount - MEMBERS_THRESHOLD
                    })}
                  </span>
                  <CaretDownIcon width={10} />
                </Styled.MembersListNameContainer>
              </Styled.MemberListItem>
            </div>
          ) : (
            <></>
          )}
        </Styled.MembersList>
      </span>
    </ClickAwayListener>
  )
}

interface PendingMemberProps {
  phoneNumber: string
}

function PendingMember(props: PendingMemberProps) {
  const color = useMemo(
    () => getRandomColor(Number(props.phoneNumber)),
    [Number(props.phoneNumber)]
  )
  return (
    <Styled.MemberListItem key={props.phoneNumber}>
      <Styled.MembersListNameContainer>
        <GroupCardAvatarContainer width={32} height={32} color={'transparent'}>
          <Avatar
            img={''}
            size="sm"
            color={color}
            name={props.phoneNumber.substring(1, 2)}
          />
        </GroupCardAvatarContainer>
        <PopupSectionRow
          title={<span style={{ marginLeft: 4 }}>{props.phoneNumber}</span>}
          onlyTitle
        />
      </Styled.MembersListNameContainer>
      <span data-content="pending">{t('invitedToGroupcal')}</span>
    </Styled.MemberListItem>
  )
}

interface MemberListRowProps {
  event?: Model<EventDoc> | EventDoc
  participant: Model<ParticipantDataDoc>
  setLoading: (b: boolean) => void
  group?: Model<GroupDoc> | GroupDoc
  withoutActions: boolean
  profile?: Model<ProfileDoc>
  actionsOnEvent?: boolean
  eventHook?: IEventDetails
  menuAnchorEl: MemberMenu | null
  setMenuAnchorEl: (el: MemberMenu | null) => void
  participants: Record<string, GroupParticipant>
  progress: any
}

function MemberListRow(props: MemberListRowProps) {
  const userName =
    props.participant.AccountID ==
    (LocalStorage.get(LocalStorageKey.USER_ID) ?? '')
      ? t('you')
      : props.participant.FullName != 'null'
        ? props.participant.FullName
        : (props.participant.PhoneNumber ?? t('tapToUpdate'))

  const [actionsOpen, setActionsOpen] = useState(false)

  const currentUserId = LocalStorage.get(LocalStorageKey.USER_ID)!
  const canMakeActions =
    (props.group?.Participants[currentUserId]?.Type ===
      GroupParticipantType.ADMIN ||
      currentUserId === props.group?.OwnerID) &&
    !props.withoutActions

  const onMemberClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    event.preventDefault()
    if (canMakeActions) {
      // Find the username span element to use as anchor
      const usernameSpan = event.currentTarget.querySelector(
        `#user-name-${props.participant.AccountID}`
      ) as HTMLElement

      if (usernameSpan) {
        props.setMenuAnchorEl?.({
          anchorEl: usernameSpan,
          participant: props.participant
        })
      }
    }
  }

  const onMemberRightClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    event.preventDefault()
    if (canMakeActions) {
      // Find the username span element to use as anchor
      const usernameSpan = event.currentTarget.querySelector(
        `#user-name-${props.participant.AccountID}`
      ) as HTMLElement

      if (usernameSpan) {
        props.setMenuAnchorEl?.({
          anchorEl: usernameSpan,
          participant: props.participant
        })
      }
    }
  }

  const color = useMemo(
    () => getRandomColor(Number(props.participant.PhoneNumber)),
    [Number(props.participant.PhoneNumber)]
  )

  const participantColor =
    props.group?.Participants[props.participant.AccountID]?.MemberColor

  let statusText = t('pending')
  let statusColor = '#808080'

  if (props.event) {
    const deliveryStatus =
      props.event.ParticipantsStatus?.[props.participant.AccountID]
        ?.ParticipantDeliveryStatus

    statusText = deliveryStatus === '9' ? t('received') : t('sent')

    if (props.event.RequestConfirmation === '1') {
      const confirmStatus =
        props.event.ParticipantsStatus?.[props.participant.AccountID]
          ?.ParticipantConfirmationStatus

      switch (confirmStatus) {
        case '2':
          statusText = t('accepted')
          statusColor = '#008000'
          break
        case '4':
          statusText = t('maybe')
          statusColor = '#D4C91D'
          break
        case '3':
          statusText = t('declined')
          statusColor = '#FF0000'
          break
        case '1':
          statusColor = '#808080'
          break
      }
    }
  }

  if (!props.group?.Participants[props.participant.AccountID]) return <></>

  if (!props.event || props.event?._id?.length === 0) statusText = ''

  return (
    <span
      onClick={onMemberClick}
      onContextMenu={onMemberRightClick}
      style={{ cursor: canMakeActions ? 'pointer' : 'default' }}
    >
      {props.group?.Participants[props.participant.AccountID].Status ===
      GroupParticipantStatus.ACTIVE ? (
        <Styled.MemberListItem key={props.participant.AccountID}>
          <Styled.MembersListNameContainer>
            <GroupCardAvatarContainer
              borderWidth="2px"
              width={32}
              height={32}
              color={participantColor ? participantColor : 'transparent'}
            >
              <Avatar
                img={
                  props.participant.AccountID ===
                  LocalStorage.get(LocalStorageKey.USER_ID)
                    ? props.profile?.ProfilePicture
                    : props.participant.PhotoURL
                }
                size="sm"
                color={color}
                name={userName}
              />
            </GroupCardAvatarContainer>
            <Box display={'flex'} flexDirection={'column'}>
              <PopupSectionRow
                onlyTitle
                cursor={canMakeActions ? 'pointer' : 'default'}
                title={
                  <Popup
                    setOpen={(b: boolean) => {
                      if (!b) props.setMenuAnchorEl(null)
                    }}
                    open={
                      props.menuAnchorEl?.participant.AccountID ===
                      props.participant.AccountID
                    }
                    content={
                      <ParticipantsActionsPopupContent
                        eventHook={props.eventHook}
                        actionsOnEvent={props.actionsOnEvent}
                        setError={props.progress.setError}
                        group={props.group!}
                        setLoading={props.setLoading}
                        setIsOpen={(open) => {
                          if (!open) props.setMenuAnchorEl?.(null)
                        }}
                        participant={
                          props.participants[props.participant.AccountID]
                        }
                        participantId={props.participant.AccountID}
                        participantInfo={props.participant}
                      />
                    }
                  >
                    <span
                      id={`user-name-${props.participant.AccountID}`}
                      style={{
                        marginLeft: 4,
                        marginRight: 4,
                        paddingRight: 4,
                        paddingLeft: 4
                      }}
                    >
                      {userName}
                    </span>
                  </Popup>
                }
              />
              {
                <span
                  style={{
                    marginLeft: 4,
                    fontSize: 12,
                    marginRight: 4,
                    color: statusColor
                  }}
                >
                  {`${statusText}`}
                </span>
              }
            </Box>
          </Styled.MembersListNameContainer>
          <span data-content="role">
            {props.participant.AccountID ===
            (props.actionsOnEvent
              ? props.eventHook?.getValues('OwnerID')
              : props.group?.OwnerID)
              ? props.event
                ? t('eventOwner')
                : t('owner')
              : props.group.Participants[props.participant.AccountID]?.Type ==
                  '2'
                ? t('admin')
                : ''}
          </span>
        </Styled.MemberListItem>
      ) : (
        <></>
      )}
    </span>
  )
}

interface MemberListProps {
  event?: Model<EventDoc> | EventDoc
  groupId?: string
  localParticipants: Model<ParticipantDataDoc>[]
  setLoading: (b: boolean) => void
  group?: Model<GroupDoc> | GroupDoc
  withoutActions: boolean
  participants?: Record<string, GroupParticipant>
  pending?: boolean
  pendingParticipants?: Record<string, GroupParticipant>
  profile?: Model<ProfileDoc>
  eventHook?: IEventDetails
  actionsOnEvent?: boolean
}

export default withObservables(['group'], (props: MemberListProps) => ({
  localParticipants: ObserveService.ParticipantsForIds(
    Object.keys(props.participants ?? props.group?.Participants ?? {})
  ),
  profile: ObserveService.Profile()
}))(MembersList)
